/**
 * Raw color primitives used to compose the semantic Light/Dark theme tokens.
 *
 * These values are intentionally sourced from colors already in use across
 * the application (see src/screens/Admin/constants/adminColors.ts and
 * src/constants/themes.ts) so the Light theme reproduces the current
 * appearance as closely as possible.
 *
 * IMPORTANT: This palette is an internal implementation detail of the theme
 * system. Components must NOT import it directly — always consume semantic
 * tokens via the `useTheme()` hook.
 */
export const palette = {
  white: "#FFFFFF",
  black: "#000000",

  // Blue / brand
  blue900: "#01346F",
  blue800: "#034895",
  blue700: "#005CBC",
  blue600: "#0061C7",
  blue200: "#A9C8F5",

  // Neutrals (light-mode leaning) — sourced from AdminColors gray scale
  gray800: "#2D2D2D",
  gray600: "#4B4B4B",
  gray500: "#585858",
  gray450: "#616161",
  gray350: "#818181",
  gray200: "#A8A8A8",
  gray150: "#C0C0C0",
  gray100: "#D0D0D0",
  gray50: "#E6E6E6",
  gray25: "#EBEBEB",
  disabledLight: "#CCCCCC",
  inputBorderLight: "#DADADA",
  contentBgLight: "#F5F9FF", // page background — matches the required light-mode page color
  elevatedLight: "#F5F9FF", // matches existing LHOverlay default panel background exactly

  // Neutrals (dark-mode leaning)
  //
  // Visual hierarchy is a 3-step "elevation" scale (each step gets
  // progressively lighter): page < surface < elevated. A soft dark
  // charcoal is used for the page background instead of pure/deep black
  // so large empty areas don't look like a void, while cards/tables/
  // inputs (surface) and modals/popovers (elevated) stay clearly
  // distinguishable from the page and from each other. The gap between
  // each step is deliberately generous (not just a couple of shades)
  // so this separation reads clearly even on large, mostly-empty module
  // landing pages. Border/divider tones sit between these surface steps
  // so lines stay visible without being harsh, and text tones keep
  // strong (but not stark-white) contrast against all three surface
  // levels.
  almostBlack: "#141414", // page background — soft dark charcoal, not pure black
  darkSurface: "#202020", // default card/table/input background — clearly lighter than page
  darkElevated: "#2B2B2B", // modals/popovers/dropdowns — clearly lighter than surface
  darkBorder: "#404040", // default border for inputs/cards/tables — visible but subtle
  darkDivider: "#363636", // hairline/divider — more subtle than border
  darkInputBorder: "#4A4A4A", // input border — a touch more prominent than the default border
  darkTextPrimary: "#F2F2F2",
  darkTextSecondary: "#B0B0B0",
  darkTextDisabled: "#6E6E6E",
  darkBrandBlue: "#3D8BFF", // lightened brand blue for adequate contrast on dark surfaces

  // Status
  green400: "#129F14",
  amber: "#FFC107",
  red700: "#AB2B09",
  infoBlue: "#0066CC", // matches existing LHOverlay/LHModalPop notification info color

  // Status banner/badge tinted backgrounds (light) — match existing LHOverlay notification colors exactly
  infoBgLight: "#E3F2FD",
  successBgLight: "#E8F5E9",
  warningBgLight: "#FFF3E0",
  errorBgLight: "#FFEBEE",

  // Status banner/badge tinted backgrounds (dark) — new, desaturated dark equivalents
  infoBgDark: "#1B2A3D",
  successBgDark: "#15301A",
  warningBgDark: "#3A2E12",
  errorBgDark: "#3A1414",

  // Overlay scrim (matches existing Figma export --bg-overlay token)
  overlayLight: "rgba(0, 0, 0, 0.5)",
  overlayDark: "rgba(0, 0, 0, 0.65)",

  // Selected/active-filter card emphasis (Light) — design-spec values for
  // highlighted KPI/summary cards (e.g. "Incomplete Loads" when that status
  // filter is applied). Distinct from `red700` (the general status.error
  // red) per the approved design spec.
  selectedCardBorderLight: "#C12C01",
  selectedCardBgLight: "#FFE5DE",

  // Readable error/negative text color already used ad hoc across this
  // app's dark-mode screens (OnTimeDispatch, LocationSummary mobile cards,
  // chart error text, toast icons) wherever default error red lacks enough
  // contrast against dark surfaces. Formalized here as a reusable token.
  darkErrorText: "#F87171",
} as const;
